/**
 * PATH: packages/ui/src/kanban/components/settings-home/settingsTestIds.ts
 * FILE_ID: UI-KANBAN-SETTINGS-TESTIDS-001
 * PURPOSE: One Settings data-testid per live control. Catalog consumers use these.
 * ROLE IN ARCHITECTURE: Shared seam ids for settings-home and reused panels.
 * MAIN EXPORTS: SETTINGS_TEST_IDS, face/drawer/board/kanban testid helpers
 * NON-RESPONSIBILITIES: Does not render or persist.
 * NOTES FOR FUTURE AI: Catalog consumer must equal a helper return. Add the
 *   testid on the control in the same change. Do not point at card/recipe names.
 *   Lane field/prompt/members/archive/landing/templates are family prefixes.
 */

import type { DockItemId } from '../board-view/dockPrefs'

export const SETTINGS_TEST_IDS = {
  faceDue: 'settings-face-due',
  faceAllocator: 'settings-face-allocator',
  faceDensity: 'settings-card-density',
  facePreview: 'settings-face-preview',
  faceTileBody: 'settings-face-tile-body',
  faceRailMode: 'settings-face-rail-mode',
  faceCompose: 'settings-face-compose',
  faceTileOpen: 'settings-face-tile-open',
  faceComplete: 'settings-face-complete',
  drawerReset: 'settings-drawer-reset',
  drawerClassic: 'settings-drawer-classic',
  boardName: 'settings-board-name',
  boardVisibility: 'settings-board-visibility',
  boardAccess: 'settings-board-access',
  boardInherited: 'settings-board-inherited-access',
  boardDescription: 'settings-board-description',
  boardDepartment: 'settings-board-department',
  boardLinkedJob: 'settings-board-linked-job',
  boardTimeMode: 'settings-board-time-mode',
  boardGanttBucket: 'settings-board-gantt-bucket',
  boardCashFlow: 'settings-board-cash-flow',
  boardTemplateState: 'settings-board-template-state',
  boardTemplateSync: 'settings-board-template-sync',
  boardTemplateDetach: 'settings-board-template-detach',
  boardBackground: 'board-bg-picker',
  boardInboxPrimary: 'board-settings-primary-inbox-select',
  boardRouting: 'board-routing-rules-section',
  boardArchivedLanes: 'settings-board-archived-lanes',
  boardArchivedCards: 'board-settings-open-archived-cards',
  boardDeletedLanes: 'settings-board-deleted-lanes',
  boardDeletedCards: 'settings-board-deleted-cards',
  boardActivity: 'board-settings-activity',
  boardRewind: 'board-settings-rewind',
  boardArchive: 'settings-board-archive',
  boardRestore: 'settings-board-restore',
  boardDelete: 'board-settings-delete-board',
  laneName: 'lane-settings-name-',
  laneDescription: 'lane-settings-description-',
  laneColour: 'lane-settings-color-',
  laneWip: 'lane-settings-wip-',
  laneDisplay: 'lane-settings-display-',
  lanePrompt: 'settings-lane-prompt-',
  laneLanding: 'settings-lane-landing-',
  laneTemplates: 'settings-lane-templates-',
  laneMembers: 'settings-lane-members-',
  laneArchive: 'settings-lane-archive-',
  openMode: 'settings-open-mode',
  dockClassic: 'settings-dock-classic',
  keyboardMode: 'settings-keyboard-mode',
  keyboardShortcuts: 'board-settings-open-shortcuts',
  hostMembers: 'settings-host-link-members',
  hostEmail: 'settings-host-link-email',
} as const

const BOARD_ENTITY_SEAMS: Record<string, string> = {
  name: SETTINGS_TEST_IDS.boardName,
  description: SETTINGS_TEST_IDS.boardDescription,
  visibility: SETTINGS_TEST_IDS.boardVisibility,
  members: SETTINGS_TEST_IDS.boardAccess,
  inheritedAccess: SETTINGS_TEST_IDS.boardInherited,
  department: SETTINGS_TEST_IDS.boardDepartment,
  linkedJob: SETTINGS_TEST_IDS.boardLinkedJob,
  timeBoard: SETTINGS_TEST_IDS.boardTimeMode,
  ganttBucket: SETTINGS_TEST_IDS.boardGanttBucket,
  cashFlow: SETTINGS_TEST_IDS.boardCashFlow,
  templateState: SETTINGS_TEST_IDS.boardTemplateState,
  background: SETTINGS_TEST_IDS.boardBackground,
  inboxPrimaryLane: SETTINGS_TEST_IDS.boardInboxPrimary,
  laneName: SETTINGS_TEST_IDS.laneName,
  laneDescription: SETTINGS_TEST_IDS.laneDescription,
  laneColour: SETTINGS_TEST_IDS.laneColour,
  laneWip: SETTINGS_TEST_IDS.laneWip,
  laneDisplayMode: SETTINGS_TEST_IDS.laneDisplay,
  lanePrompt: SETTINGS_TEST_IDS.lanePrompt,
  laneAiLanding: SETTINGS_TEST_IDS.laneLanding,
  laneTemplates: SETTINGS_TEST_IDS.laneTemplates,
  laneMembers: SETTINGS_TEST_IDS.laneMembers,
  laneArchive: SETTINGS_TEST_IDS.laneArchive,
  routingRules: SETTINGS_TEST_IDS.boardRouting,
}

const BOARD_ACTION_SEAMS: Record<string, string> = {
  templateSync: SETTINGS_TEST_IDS.boardTemplateSync,
  templateDetach: SETTINGS_TEST_IDS.boardTemplateDetach,
  archivedLanes: SETTINGS_TEST_IDS.boardArchivedLanes,
  archivedCards: SETTINGS_TEST_IDS.boardArchivedCards,
  deletedLanes: SETTINGS_TEST_IDS.boardDeletedLanes,
  deletedCards: SETTINGS_TEST_IDS.boardDeletedCards,
  activity: SETTINGS_TEST_IDS.boardActivity,
  rewind: SETTINGS_TEST_IDS.boardRewind,
  archiveBoard: SETTINGS_TEST_IDS.boardArchive,
  restoreBoard: SETTINGS_TEST_IDS.boardRestore,
  deleteBoard: SETTINGS_TEST_IDS.boardDelete,
}

const DOCK_ITEM_BY_GLOBAL: Record<string, DockItemId> = {
  'dock.myCards': 'mycards',
  'dock.planner': 'planner',
  'dock.notebooks': 'notebooks',
  'dock.filters': 'filters',
  'dock.emails': 'emails',
  'dock.board': 'board',
  'dock.time': 'gantt',
  'dock.smartTime': 'smarttime',
  'dock.timeline': 'timeline',
  'dock.swimlanes': 'swimlanes',
  'dock.table': 'table',
  'dock.calendar': 'calendar',
  'dock.cashFlow': 'cashflow',
  'dock.dashboard': 'dashboard',
  'dock.switchBoards': 'switch',
}

export function faceBlockTestId(id: string): string {
  return `settings-face-block-${id}`
}

export function faceFieldTestId(id: string): string {
  return id === 'due' ? SETTINGS_TEST_IDS.faceDue : `settings-face-field-${id}`
}

export function faceRailTestId(id: string): string {
  return id === 'allocator' ? SETTINGS_TEST_IDS.faceAllocator : `settings-face-rail-${id}`
}

export function faceStripTestId(id: string): string {
  return `settings-face-strip-${id}`
}

export function faceMenuTestId(id: string): string {
  return `settings-face-menu-${id}`
}

export function faceExtraTestId(id: string): string {
  return `settings-face-extra-${id}`
}

export function faceChromeTestId(id: string): string {
  if (id === 'tileBody') return SETTINGS_TEST_IDS.faceTileBody
  if (id === 'railMode') return SETTINGS_TEST_IDS.faceRailMode
  if (id === 'compose') return SETTINGS_TEST_IDS.faceCompose
  if (id === 'tileOpen') return SETTINGS_TEST_IDS.faceTileOpen
  if (id === 'complete') return SETTINGS_TEST_IDS.faceComplete
  return SETTINGS_TEST_IDS.facePreview
}

export function drawerBlockTestId(id: string): string {
  return `settings-drawer-block-${id}`
}

export function drawerExtraTestId(id: string): string {
  return id === 'classicExpansion'
    ? SETTINGS_TEST_IDS.drawerClassic
    : SETTINGS_TEST_IDS.drawerReset
}

export function dockItemTestId(id: DockItemId): string {
  return `settings-dock-${id}`
}

export function defaultBoardTestId(category: string): string {
  return `settings-default-board-${category.toLowerCase()}`
}

export function boardEntityTestId(id: string): string {
  const seam = BOARD_ENTITY_SEAMS[id]
  if (!seam) throw new Error(`No Settings testid for board entity ${id}`)
  return seam
}

export function boardActionTestId(id: string): string {
  const seam = BOARD_ACTION_SEAMS[id]
  if (!seam) throw new Error(`No Settings testid for board action ${id}`)
  return seam
}

export function kanbanGlobalTestId(id: string): string {
  if (id.startsWith('defaultBoard.')) {
    return defaultBoardTestId(id.slice('defaultBoard.'.length))
  }
  if (id === 'hostOpenMode') return SETTINGS_TEST_IDS.openMode
  if (id === 'classicBar') return SETTINGS_TEST_IDS.dockClassic
  if (id === 'keyboardMode') return SETTINGS_TEST_IDS.keyboardMode
  if (id === 'keyboardShortcuts') return SETTINGS_TEST_IDS.keyboardShortcuts
  if (id === 'organisationMembersLink') return SETTINGS_TEST_IDS.hostMembers
  if (id === 'emailSettingsLink') return SETTINGS_TEST_IDS.hostEmail
  const dockId = DOCK_ITEM_BY_GLOBAL[id]
  if (dockId) return dockItemTestId(dockId)
  throw new Error(`No Settings testid for ${id}`)
}

export const ALL_SETTINGS_SEAMS: readonly string[] = Object.values(SETTINGS_TEST_IDS)
